import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { IClientCompanyStore } from '../types/types';
import { createClientCompany, deleteClientCompany, getClientCompanies, updateClientCompany } from '../utils/api';

const useClientCompanies: UseBoundStore<StoreApi<IClientCompanyStore>> = create((set) => ({
  clients: [],
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  hasFetched: false,

  fetchClients: async () => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getClientCompanies();
      if (!success) throw new Error(message);
      set({
        clients: data.sort((a: any, b: any) => new Date(b.edited_at).getTime() - new Date(a.edited_at).getTime()),
        hasFetched: true,
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  addClient: async (clientData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createClientCompany(clientData);
      if (!success) throw new Error(message);
      set((state) => ({ clients: state.clients ? [...state.clients, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editClient: async (id, clientData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateClientCompany(id, clientData);
      if (!success) throw new Error(message);
      set((state) => ({
        clients: state.clients?.map((client) => (client.partnercom_id === id ? data : client)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeClient: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteClientCompany(id);
      if (!success) throw new Error(message);
      set((state) => ({
        clients: state.clients?.filter((client) => client.partnercom_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useClientCompanies;
